<script>
import { onMount } from "svelte";
import { cubicOut } from "svelte/easing";
import { prefersReducedMotion } from "svelte/motion";
import { uiState, saveUiSettings, toggleAssistant } from "@app/store/uiStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import {
    assistantState,
    getActiveChat,
    loadAssistantChats,
    refreshAssistantStatus,
    renameAssistantChat,
    startNewChat,
} from "@app/store/assistantStore.svelte.js";
import { confirmDeleteAssistantChat } from "@app/lib/assistant/confirmDeleteChat.js";
import AssistantMessages from "./AssistantMessages.svelte";
import AssistantComposer from "./AssistantComposer.svelte";
import AssistantHistory from "./AssistantHistory.svelte";
import * as m from "@app/paraglide/messages.js";

const OVERLAY_BELOW_PX = 1280;
const MIN_CHAT_WIDTH = 320;
const MIN_HISTORY_WIDTH = 160;
const MAX_HISTORY_WIDTH = 360;

let innerWidth = $state(window.innerWidth);
let resizing = $state(null); // { target: "chat" | "history", startX, startWidth }
let menuOpen = $state(false);
let editingTitle = $state(null);

let overlay = $derived(innerWidth < OVERLAY_BELOW_PX);
let showHistory = $derived(!uiState.assistant.historyCollapsed);
let maxChatWidth = $derived(Math.max(MIN_CHAT_WIDTH, Math.round(innerWidth * 0.6)));
let chatWidth = $derived(Math.min(Math.max(uiState.assistant.width, MIN_CHAT_WIDTH), maxChatWidth));
let historyWidth = $derived(
    Math.min(Math.max(uiState.assistant.historyWidth, MIN_HISTORY_WIDTH), MAX_HISTORY_WIDTH),
);
let panelWidth = $derived(chatWidth + (showHistory ? historyWidth : 0));
let activeChat = $derived(getActiveChat());

$effect(() => {
    loadAssistantChats(projectState.id);
});

onMount(() => {
    refreshAssistantStatus();
});

$effect(() => {
    // The editor area changes width with the docked panel; let size-dependent views re-measure.
    void panelWidth;
    void overlay;
    const frame = requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    return () => cancelAnimationFrame(frame);
});

/* Grows the panel from the right edge, so the docked editor area resizes with it. */
function slideFromRight(node) {
    const width = node.offsetWidth;
    return {
        duration: prefersReducedMotion.current ? 0 : 250,
        easing: cubicOut,
        css: (t) => `width: ${t * width}px; overflow: hidden;`,
    };
}

function notifyResize() {
    window.dispatchEvent(new Event("resize"));
}

function startResize(e, target) {
    e.preventDefault();
    resizing = {
        target,
        startX: e.clientX,
        startWidth: target === "chat" ? chatWidth : historyWidth,
    };
}

function onPointerMove(e) {
    if (!resizing) return;
    const delta = e.clientX - resizing.startX;
    if (resizing.target === "chat") {
        // Handle is on the left edge, so dragging left widens the panel.
        uiState.assistant.width = Math.min(Math.max(resizing.startWidth - delta, MIN_CHAT_WIDTH), maxChatWidth);
    } else {
        // History sits on the right; dragging its left edge left widens it.
        uiState.assistant.historyWidth = Math.min(
            Math.max(resizing.startWidth - delta, MIN_HISTORY_WIDTH),
            MAX_HISTORY_WIDTH,
        );
    }
}

function onPointerUp() {
    if (!resizing) return;
    resizing = null;
    saveUiSettings();
}

function toggleHistory() {
    uiState.assistant.historyCollapsed = !uiState.assistant.historyCollapsed;
    saveUiSettings();
}

function startTitleEdit() {
    menuOpen = false;
    if (activeChat) editingTitle = activeChat.title;
}

async function commitTitleEdit() {
    if (editingTitle === null || !activeChat) return;
    const title = editingTitle;
    editingTitle = null;
    await renameAssistantChat(activeChat.id, title);
}

function handleTitleKeydown(e) {
    if (e.key === "Enter") {
        e.preventDefault();
        commitTitleEdit();
    } else if (e.key === "Escape") {
        e.preventDefault();
        editingTitle = null;
    }
}

function deleteActiveChat() {
    menuOpen = false;
    confirmDeleteAssistantChat(activeChat);
}

function focusOnMount(node) {
    node.focus();
    node.select();
}

function isToggleShortcut(e) {
    return (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.code === "KeyL";
}

/* Keep editor shortcuts (Ctrl+A, Delete, …) from firing while typing in the panel. */
function isolateKeys(e) {
    const target = e.target;
    const typing = target instanceof HTMLElement && (target.matches("input, textarea") || target.isContentEditable);
    if (typing && !isToggleShortcut(e)) {
        e.stopPropagation();
    }
}

function closeMenuOnOutsideClick(e) {
    if (menuOpen && !e.target.closest?.(".assistant-menu")) {
        menuOpen = false;
    }
}
</script>

<svelte:window
    bind:innerWidth
    onpointermove={onPointerMove}
    onpointerup={onPointerUp}
    onclick={closeMenuOnOutsideClick}
/>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<aside
    class={["assistant-panel d-flex flex-column", overlay && "assistant-overlay shadow-lg", resizing && "resizing"]}
    style:width="{panelWidth}px"
    aria-label={m.ai_assistant()}
    onkeydown={isolateKeys}
    transition:slideFromRight|global
    onintroend={notifyResize}
    onoutroend={notifyResize}
>
    <div
        class="assistant-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label={m.assistant_resize()}
        onpointerdown={(e) => startResize(e, "chat")}
    ></div>

    <div class="assistant-inner d-flex flex-column flex-grow-1" style:width="{panelWidth}px">
        <header class="assistant-header d-flex align-items-center gap-1 px-2 border-bottom">
            <i class="bi bi-stars text-primary"></i>
            {#if editingTitle !== null}
                <input
                    class="form-control form-control-sm flex-grow-1"
                    aria-label={m.assistant_chat_title_label()}
                    bind:value={editingTitle}
                    onkeydown={handleTitleKeydown}
                    onblur={commitTitleEdit}
                    {@attach focusOnMount}
                />
            {:else}
                <span class="fw-semibold text-truncate flex-grow-1" title={activeChat?.title}>
                    {activeChat?.title ?? m.assistant_new_chat()}
                </span>
            {/if}
            <button
                type="button"
                class="assistant-icon-btn"
                title={m.assistant_new_chat()}
                aria-label={m.assistant_new_chat()}
                disabled={!activeChat}
                onclick={startNewChat}
            >
                <i class="bi bi-plus-lg"></i>
            </button>
            <div class="position-relative assistant-menu">
                <button
                    type="button"
                    class="assistant-icon-btn"
                    title={m.assistant_more_actions()}
                    aria-label={m.assistant_more_actions()}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    disabled={!activeChat}
                    onclick={() => (menuOpen = !menuOpen)}
                >
                    <i class="bi bi-three-dots"></i>
                </button>
                {#if menuOpen}
                    <ul class="dropdown-menu dropdown-menu-end show assistant-menu-list" role="menu">
                        <li>
                            <button type="button" class="dropdown-item" role="menuitem" onclick={startTitleEdit}>
                                <i class="bi bi-pencil me-2"></i>{m.assistant_rename_chat()}
                            </button>
                        </li>
                        <li>
                            <button type="button" class="dropdown-item text-danger" role="menuitem" onclick={deleteActiveChat}>
                                <i class="bi bi-trash me-2"></i>{m.assistant_delete_chat()}
                            </button>
                        </li>
                    </ul>
                {/if}
            </div>
            <button
                type="button"
                class={["assistant-icon-btn", showHistory && "active"]}
                title={m.assistant_toggle_history()}
                aria-label={m.assistant_toggle_history()}
                aria-pressed={showHistory}
                onclick={toggleHistory}
            >
                <i class="bi bi-layout-sidebar-reverse"></i>
            </button>
            <button
                type="button"
                class="btn-close btn-sm ms-1"
                title={m.assistant_close()}
                aria-label={m.assistant_close()}
                onclick={() => toggleAssistant(false)}
            ></button>
        </header>

        <div class="d-flex flex-grow-1 assistant-body">
            <div class="d-flex flex-column flex-grow-1 assistant-chat" style:width="{chatWidth}px">
                <AssistantMessages />
                <AssistantComposer />
            </div>
            {#if showHistory}
                <div class="position-relative flex-shrink-0" style:width="{historyWidth}px">
                    <div
                        class="assistant-resize-handle"
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={m.assistant_resize()}
                        onpointerdown={(e) => startResize(e, "history")}
                    ></div>
                    <AssistantHistory />
                </div>
            {/if}
        </div>
    </div>
</aside>

<style>
    .assistant-panel {
        position: relative;
        flex-shrink: 0;
        height: 94vh;
        background-color: var(--bs-body-bg);
        border-left: 1px solid var(--bs-border-color);
    }

    .assistant-overlay {
        position: fixed;
        right: 0;
        bottom: 0;
        z-index: 1045;
        max-width: 100vw;
    }

    .assistant-inner {
        align-self: flex-end;
        min-height: 0;
    }

    .assistant-panel.resizing {
        user-select: none;
    }

    .assistant-header {
        min-height: 2.75rem;
        background-color: var(--bs-tertiary-bg);
    }

    .assistant-icon-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.9rem;
        height: 1.9rem;
        padding: 0;
        border: 0;
        border-radius: var(--bs-border-radius);
        background: transparent;
        color: var(--bs-body-color);
    }

    .assistant-icon-btn:hover:not(:disabled),
    .assistant-icon-btn:focus-visible {
        background-color: var(--bs-secondary-bg);
        outline: none;
    }

    .assistant-icon-btn.active {
        background-color: rgba(var(--bs-primary-rgb), 0.15);
        color: var(--bs-primary);
    }

    .assistant-icon-btn:disabled {
        opacity: 0.45;
    }

    .assistant-body {
        min-height: 0;
    }

    .assistant-chat {
        min-width: 0;
    }

    .assistant-menu-list {
        position: absolute;
        right: 0;
        top: 100%;
        z-index: 1050;
    }

    .assistant-resize-handle {
        position: absolute;
        left: -3px;
        top: 0;
        bottom: 0;
        width: 6px;
        cursor: ew-resize;
        z-index: 2;
        transition: background-color 0.2s;
    }

    .assistant-resize-handle:hover,
    .resizing .assistant-resize-handle:active {
        background-color: rgba(var(--bs-primary-rgb), 0.35);
    }
</style>
